import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { InstagramEmbed } from 'react-social-media-embed';
import V2SEO from '../components/V2SEO';
import Tile from '../components/Showcase';
import FAQ from '../components/FAQ';
import LazyVideo from '../components/LazyVideo';
import AutoplayVideo from '../components/AutoplayVideo';
import {
  business,
  clients,
  featuredClients,
  showcase,
  services,
  serviceAreaCards,
  whyChoose,
  equipment,
  instagramReels,
  portfolioProjects,
  faqs,
} from '../data/realContent';

const byId = (id) => showcase.find((s) => s.id === id);
const locationOf = (matchId) =>
  portfolioProjects.find((p) => p.id === matchId)?.location;

const Home = () => {
  const mosaic = [
    { item: { ...byId('royals'), telemetry: locationOf('reading-royals') }, size: 'v2-showcase__tile--wide' },
    { item: { ...byId('weddings'), telemetry: locationOf('wedding-cinematography') }, size: 'v2-showcase__tile--tall' },
    { item: { ...byId('paving'), telemetry: locationOf('paving-infrastructure') }, size: 'v2-showcase__tile--half' },
    { item: { ...byId('fpv-construction'), telemetry: locationOf('indoor-fpv-construction') }, size: 'v2-showcase__tile--half' },
    { item: { ...byId('drift'), telemetry: locationOf('drift-coverage') }, size: 'v2-showcase__tile--tall' },
    { item: { ...byId('paintball'), telemetry: locationOf('skirmish-normandy') }, size: 'v2-showcase__tile--wide' },
  ].filter((m) => m.item && m.item.id);

  return (
    <>
      <V2SEO
        title="Cosmik Drones | Premier Drone Services in the Lehigh Valley, PA"
        description="FAA Part 107 certified drone services in the Lehigh Valley — aerial cinematography, FPV drone filming, event coverage, real estate, and construction documentation across Pennsylvania and the East Coast. Trusted by MLB, FanDuel, and The Home Depot."
        path="/"
        keywords="drone services Lehigh Valley, drone videographer Lehigh Valley, aerial cinematography Pennsylvania, FPV drone Pennsylvania, commercial drone production, event drone coverage, drone photography Allentown Bethlehem Easton"
        faqs={faqs.general}
      />

      <Hero />
      <ClientSection />
      <FeaturedCampaigns />
      <VStoryPhantoms />
      <ShowcaseSection mosaic={mosaic} />
      <ServicesGrid />
      <EquipmentSection />
      <VStoryFPV />
      <ServiceAreas />
      <WhyChoose />
      <InstagramSection />
      <FAQSection />
      <CTA />
    </>
  );
};

const Hero = () => (
  <section className="v2-hero">
    <div className="v2-hero__media">
      <AutoplayVideo src="/videos/hero-loop.mp4" poster="/images/posters/hero.jpg" />
      <div className="v2-hero__shade" />
    </div>

    <div className="v2-container v2-hero__content">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <span className="v2-hero__tagline">FAA Part 107 Certified · Fully Insured</span>
        <h1>
          Lehigh Valley's <span className="accent">Premier Drone Services</span>
        </h1>
        <p className="v2-prose" style={{ marginBottom: '1.6rem', color: 'rgba(255,255,255,0.85)' }}>
          Professional aerial cinematography and photography for commercial productions,
          live events, real estate, and construction. Trusted by MLB, FanDuel, and The Home Depot.
        </p>
        <div className="v2-hero__ctas">
          <Link to="/contact" className="v2-btn v2-btn--primary">Request a Quote</Link>
          <Link to="/portfolio" className="v2-btn v2-btn--dark">View Our Work</Link>
        </div>
      </motion.div>
    </div>

    <span className="v2-hero__scroll">Scroll</span>
  </section>
);

const ClientSection = () => (
  <section className="v2-section v2-section--tight">
    <div className="v2-container">
      <div className="v2-shead" style={{ justifyContent: 'center', textAlign: 'center' }}>
        <div style={{ width: '100%' }}>
          <span className="v2-eyebrow">Clients</span>
          <h2>Trusted by Industry Leaders</h2>
        </div>
      </div>

      <p className="v2-clients__tier">Featured Collaborations</p>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.4rem',
        marginBottom: '2.6rem',
      }}>
        {featuredClients.map((c) => (
          <div key={c.name} className="v2-clientcard v2-clientcard--featured">
            <div className="v2-clientcard__logo">
              <img src={c.logo} alt={`${c.name} logo`} loading="lazy" />
            </div>
            <p>{c.description}</p>
          </div>
        ))}
      </div>

      <p className="v2-clients__tier">Additional Partners</p>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '1rem',
      }}>
        {clients
          .filter((c) => !featuredClients.some((f) => f.name === c.name))
          .map((c) => (
            <div key={c.name} className="v2-clientcard">
              <div className="v2-clientcard__logo" style={{ height: 96 }}>
                <img src={c.logo} alt={`${c.name} logo`} loading="lazy" />
              </div>
            </div>
          ))}
      </div>
    </div>
  </section>
);

const FeaturedCampaigns = () => {
  const featured = portfolioProjects.filter((p) => p.featured);
  return (
    <section className="v2-section">
      <div className="v2-container">
        <div className="v2-shead">
          <div>
            <span className="v2-eyebrow">Featured Campaigns</span>
            <h2>Official Campaign Work</h2>
          </div>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '1.4rem',
        }}>
          {featured.map((p) => (
            <a
              key={p.id}
              href={p.externalUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                position: 'relative',
                borderRadius: '22px',
                overflow: 'hidden',
                border: '1px solid var(--v2-line)',
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
              }}
            >
              <div style={{ position: 'relative', aspectRatio: '16 / 9' }}>
                <span className="v2-telemetry">{p.location}</span>
                <img
                  src={p.thumbnail}
                  alt={p.title}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to top, rgba(7,11,22,0.9) 8%, rgba(7,11,22,0.05) 55%)',
                }} />
              </div>
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '1.4rem 1.6rem' }}>
                <p style={{ margin: '0 0 0.3rem', fontSize: '0.8rem', color: 'var(--v2-mint)' }}>{p.client}</p>
                <h3 style={{ margin: 0, fontSize: '1.35rem' }}>{p.title}</h3>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

const VStoryPhantoms = () => (
  <section className="v2-vstory">
    <div className="v2-vstory__media">
      <LazyVideo src="/videos/Phantoms website video.mp4" poster="/images/Phantoms TB toss.jpg" />
      <div className="v2-vstory__shade" />
    </div>
    <div className="v2-vstory__content">
      <div className="v2-container">
        <div className="v2-vstory__inner">
          <span className="v2-eyebrow">Sports & Live Events</span>
          <h2>Professional Event Coverage</h2>
          <p>Aerial coverage for the Lehigh Valley Phantoms, Reading Royals, Valley Preferred Velodrome, and live productions across Pennsylvania, with crowd-safe flight planning on every job.</p>
          <Link to="/services/event-drone-coverage" className="v2-btn v2-btn--primary">Event Drone Services</Link>
        </div>
      </div>
    </div>
  </section>
);

const ShowcaseSection = ({ mosaic }) => (
  <section className="v2-section">
    <div className="v2-container">
      <div className="v2-shead">
        <div>
          <span className="v2-eyebrow">Portfolio</span>
          <h2>Featured Work</h2>
        </div>
      </div>

      <div className="v2-showcase">
        {mosaic.map(({ item, size }) => (
          <Tile key={item.id} item={item} size={size} />
        ))}
      </div>

      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <Link to="/portfolio" className="v2-btn v2-btn--ghost">View Full Portfolio</Link>
      </div>
    </div>
  </section>
);

const ServicesGrid = () => (
  <section className="v2-section" id="services">
    <div className="v2-container">
      <div className="v2-shead">
        <div>
          <span className="v2-eyebrow">Services</span>
          <h2>Professional Drone Services</h2>
        </div>
      </div>
      <div className="v2-services">
        {services.map((s) => (
          <Link key={s.slug} to={`/services/${s.slug}`} className="v2-service v2-service--media">
            {s.cardImage && <img src={s.cardImage} alt="" loading="lazy" aria-hidden="true" />}
            <h3>{s.name}</h3>
            <p>{s.short}</p>
            <span className="v2-service__cta">Learn More</span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const EquipmentSection = () => (
  <section className="v2-section">
    <div className="v2-container">
      <div className="v2-shead">
        <div>
          <span className="v2-eyebrow">Equipment</span>
          <h2>Professional Equipment</h2>
        </div>
        <p>Each project is matched to the right aircraft: large-sensor camera drones for flagship aerial work, ducted cinewhoops for safe interior flight, and freestyle FPV rigs for high-speed action.</p>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.2rem',
      }}>
        {equipment.map((e) => (
          <div key={e.name} style={{
            border: '1px solid var(--v2-line)',
            borderRadius: '22px',
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.02)',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{ aspectRatio: '4 / 3', overflow: 'hidden', background: 'var(--v2-bg-elev)' }}>
              <img
                src={e.image}
                alt={e.name}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div style={{ padding: '1.3rem' }}>
              <span className="v2-eyebrow">{e.role}</span>
              <h3 style={{ margin: '0.4rem 0 0.3rem', fontSize: '1.1rem' }}>{e.name}</h3>
              <p style={{ margin: 0, color: 'var(--v2-text-dim)', fontSize: '0.9rem' }}>{e.notes}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const VStoryFPV = () => (
  <section className="v2-vstory">
    <div className="v2-vstory__media">
      <LazyVideo src="/videos/Construction website indoor fpv.mp4" poster="/images/posters/fpv-construction.jpg" />
      <div className="v2-vstory__shade v2-vstory__shade--right" />
    </div>
    <div className="v2-vstory__content">
      <div className="v2-container">
        <div className="v2-vstory__inner v2-vstory__inner--right">
          <span className="v2-eyebrow">FPV Cinematography</span>
          <h2>Immersive FPV Drone Footage</h2>
          <p>Specialized first-person-view flying for indoor spaces, tight proximity work, and dynamic action sequences that traditional drones cannot capture.</p>
          <Link to="/services/fpv-drone-filming" className="v2-btn v2-btn--primary">FPV Drone Services</Link>
        </div>
      </div>
    </div>
  </section>
);

const ServiceAreas = () => (
  <section className="v2-section">
    <div className="v2-container">
      <div className="v2-shead">
        <div>
          <span className="v2-eyebrow">Coverage</span>
          <h2>Serving the Lehigh Valley & Beyond</h2>
        </div>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.2rem',
      }}>
        {serviceAreaCards.map((a) => (
          <div key={a.title} style={{
            border: '1px solid var(--v2-line)',
            borderRadius: '22px',
            padding: '1.6rem',
            background: 'rgba(255,255,255,0.02)',
          }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.15rem' }}>{a.title}</h3>
            <p style={{ margin: 0, color: 'var(--v2-text-dim)', fontSize: '0.92rem', lineHeight: 1.6 }}>
              {a.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyChoose = () => (
  <section className="v2-section">
    <div className="v2-container">
      <div className="v2-shead">
        <div>
          <span className="v2-eyebrow">Why Cosmik Drones</span>
          <h2>Built on Certification, Equipment, and Craft</h2>
        </div>
      </div>
      <div className="v2-trust">
        {whyChoose.map((w) => (
          <div key={w.title} className="v2-trust__item">
            <strong>{w.title}</strong>
            <span>{w.description}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Instagram embeds are third-party iframes — mount them only when the
// section approaches the viewport so they never slow initial page load.
const InstagramSection = () => {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin: '600px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="v2-section" ref={ref}>
      <div className="v2-container">
        <div className="v2-shead">
          <div>
            <span className="v2-eyebrow">Instagram</span>
            <h2>Latest from the Field</h2>
          </div>
          <p>Aerial captures and behind-the-scenes from recent flights.</p>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1rem',
        }}>
          {mounted
            ? instagramReels.map((url) => (
                <div key={url} style={{
                  borderRadius: '18px',
                  overflow: 'hidden',
                  border: '1px solid var(--v2-line)',
                  background: 'var(--v2-bg-card)',
                  minHeight: 420,
                }}>
                  <InstagramEmbed url={url} width="100%" captioned />
                </div>
              ))
            : instagramReels.map((url) => (
                <div key={url} style={{
                  borderRadius: '18px',
                  border: '1px solid var(--v2-line)',
                  background: 'var(--v2-bg-card)',
                  minHeight: 420,
                }} />
              ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '1.8rem' }}>
          <a
            href={business.social.instagram}
            target="_blank"
            rel="noreferrer"
            className="v2-btn v2-btn--ghost"
          >
            Follow on Instagram
          </a>
        </div>
      </div>
    </section>
  );
};

const FAQSection = () => (
  <section className="v2-section">
    <div className="v2-container">
      <div className="v2-shead">
        <div>
          <span className="v2-eyebrow">FAQ</span>
          <h2>Frequently Asked Questions</h2>
        </div>
      </div>
      <FAQ items={faqs.general} />
    </div>
  </section>
);

const CTA = () => (
  <section className="v2-section v2-section--tight">
    <div className="v2-container">
      <div className="v2-cta">
        <h2>Ready to discuss your project?</h2>
        <p>Most inquiries receive a response within one business day.</p>
        <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap' }}>
          <Link to="/contact" className="v2-btn v2-btn--primary">Request a Quote</Link>
          <a href={business.phoneHref} className="v2-btn v2-btn--ghost">Call {business.phone}</a>
        </div>
      </div>
    </div>
  </section>
);

export default Home;
